import { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import api from '../../../../api/api';
import { Contact } from '../../../../model/Contact';
import { ContactListSummary, List } from '../../../../model/List';
import { to } from '../../../../util/util';

// the server's Contact also sends these (investi.Core Email/Contact.cs); the v1 model doesn't list them
export interface ContactRecord extends Contact {
    consentSource?: string;
    unsubscribeReason?: string;
}

// one person across every list they're on (contacts are stored per list, keyed by email)
export interface ContactRow {
    email: string;
    name: string;
    // subscribed on any list counts as subscribed
    subscribed: boolean;
    created: string;
    tags: string[];
    listIds: string[];
    consentSource?: string;
    unsubscribeReason?: string;
}

export interface ContactsData {
    lists: ContactListSummary[];
    // the default list (ASX announcement sends) has no name
    defaultListId?: string;
    contacts: ContactRow[];
}

export const listName = (l: ContactListSummary) => l.name || 'Investors';

export const contactKey = (email: string) => encodeURIComponent(email.toLowerCase());

export const initials = (name: string, email: string) => {
    const words = (name || email.split('@')[0]).replace(/[^A-Za-z\s.]/g, ' ').split(/[\s.]+/).filter(Boolean);
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
};

export const splitName = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
};

export const isNewInDays = (c: ContactRow, days: number) => moment().diff(moment(c.created), 'days') < days;

// reads only: GET /api/ContactList/Lists, then GET /api/ContactList for each list (both need the client's Edit Mailchimp role)
export const useContactsData = (stockId?: string) => {
    const [data, setData] = useState<ContactsData | null>(null);
    const [error, setError] = useState('');

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stockId) return;
        setData(null);
        setError('');
        const [listErr, listsResult] = await to(api().getContactLists(stockId));
        const lists = listsResult as ContactListSummary[] | undefined;
        if (listErr || !lists) {
            if (isMounted()) setError(listErr?.message || 'Could not load contacts');
            return;
        }
        const [membersErr, membersResult] = await to(Promise.all(lists.map(l => api().getContactList(stockId, l.id))));
        const members = membersResult as List[] | undefined;
        if (!isMounted()) return;
        if (membersErr || !members) {
            setError(membersErr?.message || 'Could not load contacts');
            return;
        }
        const byEmail = new Map<string, ContactRow>();
        members.forEach(list => (list.subscribers as ContactRecord[]).forEach(s => {
            const key = s.email.toLowerCase();
            const row = byEmail.get(key);
            if (!row) {
                byEmail.set(key, {
                    email: s.email, name: s.name || '', subscribed: s.status === 'subscribed', created: s.created,
                    tags: [...(s.tags || [])], listIds: [list.id], consentSource: s.consentSource, unsubscribeReason: s.unsubscribeReason,
                });
                return;
            }
            row.listIds.push(list.id);
            row.subscribed = row.subscribed || s.status === 'subscribed';
            if (!row.name && s.name) row.name = s.name;
            if (moment(s.created).isBefore(row.created)) row.created = s.created;
            (s.tags || []).forEach(t => { if (!row.tags.includes(t)) row.tags.push(t); });
            row.consentSource = row.consentSource || s.consentSource;
        }));
        const contacts = Array.from(byEmail.values()).sort((a, b) => moment(b.created).diff(moment(a.created)));
        setData({ lists, defaultListId: lists.find(l => !l.name)?.id, contacts });
    }, [stockId]);

    return { data, error, setData };
};

// a small CSV of the given rows (Export); reads nothing from the server
export const contactsCsv = (rows: ContactRow[], lists: ContactListSummary[], withTags: boolean) => {
    const cell = (v: string) => /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const names = new Map(lists.map(l => [l.id, listName(l)]));
    const head = ['Name', 'Email', 'Status', 'Lists', 'Added', ...(withTags ? ['Tags', 'Consent'] : [])];
    const body = rows.map(r => [r.name, r.email, r.subscribed ? 'Subscribed' : 'Unsubscribed', r.listIds.map(id => names.get(id) ?? '').join('; '),
        moment(r.created).format('YYYY-MM-DD'), ...(withTags ? [r.tags.join('; '), r.consentSource ?? ''] : [])]);
    return [head, ...body].map(line => line.map(cell).join(',')).join('\r\n');
};
